import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api, formatApiError, PLATFORM_META, BACKEND_URL } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { PlatformLogo } from "../components/PlatformLogo";
import { ExternalLink, Check, Sparkles, Star, ArrowRight, ShieldCheck, Camera, X, Zap } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1566371486490-560ded23b5e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHRvdXJpc3RzJTIwdmFjYXRpb258ZW58MHx8fHwxNzg3MjA2MjIwfDA&ixlib=rb-4.1.0&q=85";
const THANKS =
  "https://images.unsplash.com/flagged/photo-1573998594056-53123f2fedec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxoYXBweSUyMHRvdXJpc3RzJTIwdmFjYXRpb258ZW58MHx8fHwxNzg3MjA2MjIwfDA&ixlib=rb-4.1.0&q=85";

const PlatformButton = ({ p, disabled, onClick, t }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={`platform-btn-${p.key}`}
    className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 text-left transition-colors disabled:opacity-50 border-slate-200 bg-white hover:border-slate-400"
  >
    <PlatformLogo platform={p.key} color={p.color} />
    <div className="flex-1 min-w-0">
      <div className="font-display font-semibold text-[#0A2540]">{p.label}</div>
      <div className="text-xs text-slate-500">{t("platform_tap_to_publish")}</div>
    </div>
    <ExternalLink className="w-5 h-5 text-slate-400 shrink-0" />
  </button>
);

export default function ReviewFlow() {
  const { tourId } = useParams();
  const [params] = useSearchParams();
  const source = (params.get("src") || "email").toLowerCase();
  const { t } = useLang();
  const [tour, setTour] = useState(null);
  const [error, setError] = useState(null);
  const [exhausted, setExhausted] = useState(false);
  // steps: 0 welcome, 1 write + platforms, 2.5 private, 3 done
  const [step, setStep] = useState(0);
  const [text, setText] = useState("");
  const [rating, setRating] = useState(0);
  const [donePlatforms, setDonePlatforms] = useState(new Set());
  const [privateEmail, setPrivateEmail] = useState("");
  const [privateName, setPrivateName] = useState("");
  const [submittingPrivate, setSubmittingPrivate] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const storageKey = `rf_progress_${tourId}`;
  const restoredRef = useRef(false);

  // Restore progress if the browser reloaded the page while the customer was on a platform
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
      if (saved && Date.now() - saved.ts < 24 * 3600 * 1000 && saved.step > 0 && saved.step < 3) {
        setStep(saved.step === 2 ? 1 : saved.step);
        setText(saved.text || "");
        setRating(saved.rating || 0);
        setDonePlatforms(new Set(saved.done || []));
        setPhotos(saved.photos || []);
        restoredRef.current = true;
      }
    } catch {}
  }, [storageKey]);

  useEffect(() => {
    if (step === 3) { localStorage.removeItem(storageKey); return; }
    if (step === 0) return;
    localStorage.setItem(storageKey, JSON.stringify({
      ts: Date.now(), step, text, rating,
      done: [...donePlatforms],
      photos: photos.map(({ id, url }) => ({ id, url })),
    }));
  }, [storageKey, step, text, rating, donePlatforms, photos]);

  const availablePlatforms = useMemo(() => {
    if (!tour) return [];
    return Object.keys(PLATFORM_META)
      .filter((k) => tour.platforms?.[k] && tour.platforms[k].startsWith("http"))
      .map((k) => ({ key: k, ...PLATFORM_META[k], url: tour.platforms[k] }));
  }, [tour]);

  const threshold = tour?.rating_threshold ?? 4;
  const isLowRating = rating > 0 && rating < threshold;
  const canPublish = text.length >= 20 && rating >= 1 && !isLowRating;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/tours/${tourId}`);
        setTour(data);
        if (data.credits_exhausted) { setExhausted(true); return; }
        if (restoredRef.current) return;
        api.post(`/public/tours/${tourId}/track`, { type: "opened" }).catch((e) => {
          if (e?.response?.status === 402) setExhausted(true);
        });
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [tourId]);

  const uploadPhoto = async (file) => {
    if (photos.length >= 3) { toast.error(t("photos_max_reached")); return; }
    if (!file.type.startsWith("image/")) { toast.error("Invalid file type"); return; }
    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/public/tours/${tourId}/photos`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((s) => [...s, { id: data.id, url: `${BACKEND_URL}${data.url}`, blobUrl: URL.createObjectURL(file) }]);
    } catch (e) {
      if (e?.response?.status === 402) setExhausted(true);
      else toast.error(formatApiError(e));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const openPlatform = async (p) => {
    try { await navigator.clipboard.writeText(text); toast.success(t("copied")); } catch {}
    api.post(`/public/tours/${tourId}/track`, { type: "platform_click", platform: p.key }).catch(() => {});
    api.post(`/public/tours/${tourId}/track`, { type: "copied" }).catch(() => {});
    setDonePlatforms((s) => new Set([...s, p.key]));
    window.open(p.url, "_blank", "noopener,noreferrer");
  };

  const finish = () => {
    api.post(`/public/tours/${tourId}/track`, {
      type: "completed", source, photos: photos.length, platforms_used: donePlatforms.size,
    }).catch(() => {});
    setStep(3);
  };

  const submitPrivate = async () => {
    if (text.length < 1 || rating < 1) return;
    setSubmittingPrivate(true);
    try {
      await api.post(`/public/tours/${tourId}/private-feedback`, {
        rating, text, contact_email: privateEmail, contact_name: privateName,
      });
      api.post(`/public/tours/${tourId}/track`, {
        type: "completed", source, photos: photos.length, platforms_used: 0,
      }).catch(() => {});
      toast.success(t("private_thanks"));
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmittingPrivate(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFFDF9] grid place-items-center px-6 text-center">
        <div>
          <div className="font-display text-2xl font-bold text-[#0A2540]">{t("tour_not_found")}</div>
          <p className="text-slate-500 mt-2">{error}</p>
        </div>
      </div>
    );
  }
  if (!tour) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  if (exhausted) {
    return (
      <div className="min-h-screen bg-[#FFFDF9] grid place-items-center px-6 text-center" data-testid="credits-exhausted-screen">
        <div className="max-w-sm">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#E57C4F]/10 grid place-items-center">
            <Zap className="w-8 h-8 text-[#E57C4F]" />
          </div>
          <h1 className="mt-6 font-display text-3xl font-bold text-[#0A2540] tracking-tight">{t("credits_exhausted_title")}</h1>
          <p className="mt-3 text-slate-600">{t("credits_exhausted_desc")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF9] relative">
      <div className="absolute top-4 right-4 z-30">
        <LanguageSwitcher variant="glass" />
      </div>

      <div className="max-w-md mx-auto min-h-screen relative pb-28">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.4 }}>
              <div className="relative h-72 overflow-hidden">
                <img src={HERO} alt="Tour" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FFFDF9] via-[#FFFDF9]/20 to-transparent" />
              </div>
              <div className="px-8 -mt-16 relative z-10">
                <div className="inline-flex items-center gap-1.5 glass rounded-full px-3 py-1.5 text-xs font-semibold text-[#0A2540] mb-4">
                  <Sparkles className="w-3.5 h-3.5 text-[#E57C4F]" /> {tour.name}
                </div>
                <h1 className="font-display text-4xl font-bold text-[#0A2540] leading-tight tracking-tight">{t("welcome_hi")}</h1>
                <p className="mt-2 text-slate-600">
                  {t("welcome_sub")} <span className="font-semibold text-[#0A2540]">{tour.business_name}</span>
                </p>
                <p className="mt-6 text-slate-600 leading-relaxed">{t("welcome_desc")}</p>
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.4 }} className="px-6 sm:px-8 pt-16">
              <h2 className="font-display text-3xl font-bold text-[#0A2540] tracking-tight">{t("step_write_title")}</h2>
              <p className="mt-2 text-slate-600">{t("step_write_desc")}</p>

              <div className="mt-6 flex gap-1.5" data-testid="rating-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`} className="transition-transform hover:scale-110">
                    <Star className={`w-8 h-8 ${n <= rating ? "fill-[#E57C4F] text-[#E57C4F]" : "text-slate-300"}`} />
                  </button>
                ))}
              </div>

              <Textarea
                data-testid="review-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t("placeholder")}
                className="mt-6 min-h-[180px] text-base rounded-xl border-slate-300 focus-visible:ring-[#2B8C9F]"
              />
              <div className="mt-2 text-xs text-slate-500">
                {text.length < 20 ? `${t("min_chars")} · ${text.length}/20` : `${text.length}`}
              </div>

              <div className="mt-6" data-testid="photos-section">
                <div className="font-display font-semibold text-[#0A2540]">{t("photos_title")}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t("photos_desc")}</div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  {photos.map((p) => (
                    <div key={p.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200" data-testid={`photo-${p.id}`}>
                      <a href={p.url} target="_blank" rel="noreferrer" download>
                        <img src={p.blobUrl || p.url} alt="" className="w-full h-full object-cover" />
                      </a>
                      <button
                        type="button"
                        onClick={() => setPhotos((s) => s.filter((x) => x.id !== p.id))}
                        data-testid={`remove-photo-${p.id}`}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80"
                        aria-label={t("photos_remove")}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {photos.length < 3 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      data-testid="add-photo-btn"
                      className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-[#2B8C9F] hover:bg-slate-50 grid place-items-center text-slate-500 transition-colors disabled:opacity-50"
                    >
                      {uploadingPhoto ? <span className="text-[10px]">{t("photos_uploading")}</span> : <Camera className="w-5 h-5" />}
                    </button>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  ref={fileInputRef}
                  data-testid="photo-file-input"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }}
                  className="hidden"
                />
              </div>

              {!isLowRating && (
                <div className="mt-8" data-testid="platforms-list">
                  <div className="font-display font-semibold text-[#0A2540]">{t("platforms_list_title")}</div>
                  <div className="text-xs text-slate-500 mt-0.5 mb-3">
                    {canPublish ? t("platforms_list_desc") : t("platforms_list_locked")}
                  </div>
                  {availablePlatforms.length === 0 ? (
                    <div className="p-4 bg-white border border-slate-200 rounded-xl text-slate-600 text-sm">{t("no_platforms")}</div>
                  ) : (
                    <div className="space-y-3">
                      {availablePlatforms.map((p) => (
                        <PlatformButton key={p.key} p={p} t={t} disabled={!canPublish} onClick={() => openPlatform(p)} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {step === 2.5 && (
            <motion.div key="s25" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.4 }} className="px-8 pt-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2B8C9F]/10 text-[#2B8C9F] text-xs font-semibold mb-4">
                <ShieldCheck className="w-3.5 h-3.5" /> {t("private_100")}
              </div>
              <h2 className="font-display text-3xl font-bold text-[#0A2540] tracking-tight">{t("private_title")}</h2>
              <p className="mt-2 text-slate-600">{t("private_desc")}</p>
              <div className="mt-6 bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-xs text-slate-500 mb-2">{t("your_review")}</div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{text}</div>
                <div className="mt-3 flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-4 h-4 ${n <= rating ? "fill-[#E57C4F] text-[#E57C4F]" : "text-slate-300"}`} />
                  ))}
                </div>
              </div>
              <div className="mt-5 space-y-3">
                <Input data-testid="private-name-input" value={privateName} onChange={(e) => setPrivateName(e.target.value)} placeholder={t("private_name")} className="h-11" />
                <Input type="email" data-testid="private-email-input" value={privateEmail} onChange={(e) => setPrivateEmail(e.target.value)} placeholder={t("private_email")} className="h-11" />
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} className="px-8 pt-12" data-testid="review-finished">
              <div className="rounded-3xl overflow-hidden mb-8 shadow-lg">
                <img src={THANKS} alt="Thanks" className="w-full h-56 object-cover" />
              </div>
              <h2 className="font-display text-4xl font-bold text-[#0A2540] tracking-tight">{t("finish_title")}</h2>
              <p className="mt-3 text-slate-600 text-lg">{t("finish_desc")}</p>
              <div className="mt-8 flex justify-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className={`w-8 h-8 ${n <= (rating || 5) ? "fill-[#E57C4F] text-[#E57C4F]" : "text-slate-300"}`} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {step < 3 && (
          <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md z-20 px-4 sm:px-8 py-4 glass border-t border-white/40">
            <div className="flex gap-2">
              {(step === 1 || step === 2.5) && (
                <Button variant="outline" onClick={() => setStep(step === 2.5 ? 1 : 0)} data-testid="review-back-btn" className="rounded-full h-12 px-6">
                  {t("back")}
                </Button>
              )}
              {step === 0 && (
                <Button onClick={() => setStep(1)} data-testid="review-start-btn" className="rounded-full h-12 flex-1 bg-[#E57C4F] hover:bg-[#E57C4F]/90 text-white text-base font-semibold gap-2">
                  {t("start")} <ArrowRight className="w-4 h-4" />
                </Button>
              )}
              {step === 1 && isLowRating && (
                <Button onClick={() => setStep(2.5)} disabled={text.length < 20} data-testid="review-next-btn" className="rounded-full h-12 flex-1 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white text-base font-semibold gap-2 disabled:opacity-50">
                  {t("next")} <ArrowRight className="w-4 h-4" />
                </Button>
              )}
              {step === 1 && !isLowRating && (
                <Button onClick={finish} disabled={!canPublish} data-testid="review-finish-btn" className="rounded-full h-12 flex-1 bg-[#10B981] hover:bg-[#10B981]/90 text-white text-base font-semibold gap-2 disabled:opacity-50">
                  {t("finish_now_btn")} <Check className="w-4 h-4" />
                </Button>
              )}
              {step === 2.5 && (
                <Button onClick={submitPrivate} disabled={submittingPrivate} data-testid="private-submit-btn" className="rounded-full h-12 flex-1 bg-[#2B8C9F] hover:bg-[#2B8C9F]/90 text-white text-base font-semibold">
                  {submittingPrivate ? "…" : t("private_submit")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
